import React, { useEffect, useRef, useState } from 'react';
import { FaRegCalendar } from 'react-icons/fa';
import { KeyBoard } from './KeyBoard';
import { formatDateFieldValue } from './utils';
import {
  LabelledFormElement,
  LabelledFormElementProps,
  MultilangFormEditData,
} from '@carlito/dashboard-ui';
import { useFormikContext } from 'formik';
import { isMultiLangContent } from '@carlito/intl';

interface Field {
  name: 'day' | 'month' | 'year';
  placeholder: string;
  length: number;
}

export type CustomDateInputFieldProps = {
  value?: string;
  onChange?: (value: React.ChangeEvent<HTMLInputElement>) => void;
} & JSX.IntrinsicElements['input'] &
  LabelledFormElementProps;

export const CustomDateInputField = ({
  value,
  label,
  labelClassName,
  className,
  disableToggle,
  loading,
  name = '',
  ...rest
}: CustomDateInputFieldProps) => {
  const { values, getFieldProps, setFieldValue, setFieldTouched } =
    useFormikContext<MultilangFormEditData<any>>();

  const [date, setDate] = useState({ day: '', month: '', year: '' });
  const [selectedField, setSelectedField] = useState<'day' | 'month' | 'year'>();
  const [isFocused, setIsFocused] = useState(false);
  const hiddenInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (value && value.split('-').length === 3) {
      const [year, month, day] = value.split('-');
      setDate({ day, month, year });
    }
  }, [value]);

  const handleOnChange = (newValue: string) => {
    const event = {
      target: {
        name,
        value: newValue,
      },
    } as React.ChangeEvent<HTMLInputElement>;

    const { value: formikValue, onChange } = getFieldProps(name);

    if (isMultiLangContent(formikValue)) {
      setFieldValue(name, {
        ...formikValue,
        values: {
          ...formikValue.values,
          [values.activeLanguage]: event.target.value,
        },
      });
    } else {
      onChange(event);
      rest?.onChange?.(event);
    }
    setFieldTouched(name, true);
  };

  const handleKeyDown = (value: string) => {
    if (!selectedField) return;

    if (value === 'Close') {
      setIsFocused(false);
      setSelectedField(undefined);
      hiddenInputRef.current?.blur();
      console.log('handleKeyPress close');
      return;
    }

    if (value === '{bksp}') {
      setDate((prevDate) => ({ ...prevDate, [selectedField]: '' }));
      handleOnChange('');
      return;
    }

    if (isNaN(Number(value))) return;

    const prevValue = date[selectedField];
    const newValue = formatDateFieldValue(prevValue, value, selectedField);

    if (selectedField === 'year' && newValue.length === 4 && date.day && date.month) {
      setIsFocused(false);
      setSelectedField(undefined);
      hiddenInputRef.current?.blur();
    }

    if (newValue.length === 2 && selectedField !== 'year') {
      setSelectedField(selectedField === 'day' ? 'month' : 'year');
    }

    const newDate = { ...date, [selectedField]: newValue };
    const isValidDate = newDate.day && newDate.month && newDate.year;
    handleOnChange(isValidDate ? `${newDate.year}-${newDate.month}-${newDate.day}` : '');
    setDate(newDate);
  };

  const handleDivClick = (field: 'day' | 'month' | 'year') => {
    setSelectedField(field);
    setIsFocused(true);
    hiddenInputRef.current?.focus();
  };

  const formatValue = (value: string, length: number) => {
    return value.padStart(length, '0');
  };

  const fields: Field[] = [
    { name: 'day', placeholder: 'dd', length: 2 },
    { name: 'month', placeholder: 'mm', length: 2 },
    { name: 'year', placeholder: 'yyyy', length: 4 },
  ];

  return (
    <React.Fragment>
      <LabelledFormElement
        labelClassName={labelClassName}
        className={className}
        disableToggle={disableToggle}
        loading={loading}
        label={label}
      >
        <div
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            setIsFocused(true);
            hiddenInputRef.current?.focus();
            if (!selectedField) setSelectedField('day');
          }}
          className={`flex justify-between bg-white pl-3 pr-3.5 w-full rounded-md sm:text-sm border border-gray-300 flex-1 items-center ${
            isFocused
              ? 'border-indigo-500 outline-2 ring-1 outline-indigo-500 border ring-indigo-500'
              : ''
          }`}
        >
          <div className={'flex'}>
            <div className="flex items-center justify-start text-md">
              {fields.map((field, index) => (
                <React.Fragment key={field.name}>
                  <div
                    tabIndex={-1}
                    onClick={(e) => {
                      handleDivClick(field.name as 'day' | 'month' | 'year');
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onMouseDown={(e) => e.preventDefault()}
                    className={`cursor-default px-0.5 ${
                      selectedField === field.name ? 'bg-blue-200' : ''
                    }`}
                  >
                    {date[field.name]
                      ? formatValue(date[field.name], field.length)
                      : field.placeholder}
                  </div>
                  {index < fields.length - 1 && <span className="text-md">/</span>}
                </React.Fragment>
              ))}
            </div>
            <input
              name={name}
              value={value}
              {...rest}
              onBlur={(e) => {
                setIsFocused(false);
                setSelectedField(undefined);
                rest?.onBlur?.(e);
              }}
              ref={hiddenInputRef}
              type="date"
              className="text-sm opacity-0 w-0"
            />
          </div>

          <FaRegCalendar className={'text-xs'} />
        </div>
      </LabelledFormElement>
      {isFocused && (
        <KeyBoard
          canChangeKeyboardType={false}
          keyboardType={'Number'}
          handleKeyPress={(key) => {
            handleKeyDown(key);
          }}
        />
      )}
    </React.Fragment>
  );
};
